import React, { useState } from 'react';
import { Modal, ModalDialog, ModalClose, Typography, Select, Option, Box, Button, IconButton } from '@mui/joy';
import LifeAreaRemediesTable from './LifeAreaRemediesTable';
import { PlanetName } from '../utils/astroEngine';

interface RemediesModalProps {
  open: boolean;
  onClose: () => void;
  houseScores: Record<number, number>;
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  houseAspects?: Record<number, string[]>;
  planetStrengths?: Partial<Record<PlanetName, number>>;
  getPlanetColor?: (planet: string) => string;
}

const LIFE_AREAS = [
  { name: 'Health', houses: [1, 6, 8, 12], icon: '❤️' },
  { name: 'Wealth', houses: [1, 2, 5, 9, 11, 12], icon: '💰' },
  { name: 'Job', houses: [1, 6, 10], icon: '💼' },
  { name: 'Business', houses: [1, 3, 7, 11], icon: '📈' },
  { name: 'Marriage', houses: [1, 7], icon: '💑' },
  { name: 'Progeny', houses: [1, 5], icon: '👶' }
];

export default function RemediesModal(props: RemediesModalProps) {
  const { open, onClose, houseScores, placements, houseLords, houseAspects, planetStrengths, getPlanetColor } = props;
  const [selectedLifeArea, setSelectedLifeArea] = useState<string>('ALL');

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10003 }}>
      <ModalDialog
        sx={{
          maxWidth: '90vw',
          width: { xs: '100vw', sm: '95vw', md: '85vw', lg: '1100px' },
          maxHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 120px)', md: '85vh' },
          height: { xs: 'calc(100vh - 56px)', sm: 'auto' },
          mt: { xs: '56px', sm: 4, md: 4 },
          borderRadius: { xs: 0, sm: 'md' },
          overflow: 'auto',
          p: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Typography level="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, flex: 1 }}>
            🌿 Life Area Remedies
          </Typography>
          <IconButton
            onClick={onClose}
            size="sm"
            variant="plain"
            color="neutral"
            sx={{
              bgcolor: { xs: 'background.surface', sm: 'transparent' },
              border: { xs: '2px solid', sm: 'none' },
              borderColor: { xs: 'neutral.outlinedBorder', sm: 'transparent' },
              boxShadow: { xs: 'sm', sm: 'none' },
              width: { xs: 36, sm: 32 },
              height: { xs: 36, sm: 32 },
              minWidth: { xs: 36, sm: 32 },
              minHeight: { xs: 36, sm: 32 },
              borderRadius: 'sm',
              flexShrink: 0,
              '&:hover': {
                bgcolor: { xs: 'neutral.softBg', sm: 'background.level1' },
                borderColor: { xs: 'neutral.outlinedHoverBorder', sm: 'transparent' }
              },
              '& svg': {
                fontSize: { xs: '1.25rem', sm: '1.125rem' }
              }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </IconButton>
        </Box>

        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' }, 
          gap: 2 
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: { xs: 1, sm: 2 }, 
            flexShrink: 0 
          }}>
            <Typography level="body-sm" sx={{ 
              fontWeight: 600, 
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              Select Life Area:
            </Typography>
            <Select
              value={selectedLifeArea}
              onChange={(_, value) => setSelectedLifeArea(value as string)}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size="sm"
              slotProps={{
                listbox: {
                  placement: 'bottom-start',
                  disablePortal: false,
                  sx: { zIndex: 10000 }
                }
              }}
            >
              <Option value="ALL">🌟 All Areas</Option>
              {LIFE_AREAS.map((area) => (
                <Option key={area.name} value={area.name}>
                  {area.icon} {area.name}
                </Option>
              ))}
            </Select>
          </Box>
          <Box sx={{ 
            p: { xs: 1.25, sm: 1.5 }, 
            bgcolor: 'primary.softBg', 
            borderRadius: 'sm',
            borderLeft: '3px solid',
            borderColor: 'primary.500',
            flex: 1
          }}>
            <Typography level="body-xs" sx={{ 
              color: 'primary.700', 
              lineHeight: 1.5,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              💡 <strong>Personalized Guidance:</strong> Balance all your life areas with remedies for planets showing negative scores (red highlights). Look for the <strong>"Important"</strong> tag to identify remedies specifically tailored for you based on your chart's weak areas.
            </Typography>
          </Box>
        </Box>

        <FilteredRemediesTable
          selectedLifeArea={selectedLifeArea}
          houseScores={houseScores}
          placements={placements}
          houseLords={houseLords}
          houseAspects={houseAspects}
          planetStrengths={planetStrengths}
          getPlanetColor={getPlanetColor}
        />
      </ModalDialog>
    </Modal>
  );
}

interface FilteredRemediesTableProps {
  selectedLifeArea: string;
  houseScores: Record<number, number>;
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  houseAspects?: Record<number, string[]>;
  planetStrengths?: Partial<Record<PlanetName, number>>;
  getPlanetColor?: (planet: string) => string;
}

function FilteredRemediesTable(props: FilteredRemediesTableProps) {
  const { selectedLifeArea, houseScores, placements, houseLords, houseAspects, planetStrengths, getPlanetColor } = props;

  // If ALL is selected, show the full table
  if (selectedLifeArea === 'ALL') {
    return (
      <LifeAreaRemediesTable
        houseScores={houseScores}
        placements={placements}
        houseLords={houseLords}
        houseAspects={houseAspects}
        planetStrengths={planetStrengths}
        getPlanetColor={getPlanetColor}
      />
    );
  }

  // Filter to show only the selected life area
  return (
    <LifeAreaRemediesTable
      houseScores={houseScores}
      placements={placements}
      houseLords={houseLords}
      houseAspects={houseAspects}
      planetStrengths={planetStrengths}
      getPlanetColor={getPlanetColor}
      filterLifeArea={selectedLifeArea}
    />
  );
}
